import { globalConfigReducer as globalConfig } from "../reducers/globalConfigReducer";
import { default as settings } from "../features/settings/settingsSlice";
import { testResultReducer as testResult } from "../reducers/testResultReducer";
import { testInputReducer as testInput } from "../reducers/testInputReducer";
import { default as oauth } from "../features/oauth/oauthSlice";
import { default as proofOfWork } from "../features/proofOfWork/proofOfWorkSlice";
import { default as testSelection } from "../features/testSelection/testSelectionSlice";
import initialState from "./initialState";
import { IRcaBootConfig } from "../boot";
import { IServices } from "../services/IServices";
import { Utils } from "../Utils";
import { configureStore } from "@reduxjs/toolkit";
import { toThemeName } from "../services/ThemeService";

/**
 * Creates the redux store for the application
 *
 * @param config - The boot configuration
 * @param services - The injected services
 * @returns - the configured redux store
 */
export const getStore = (config: IRcaBootConfig, services: IServices) => {
  const state = { ...initialState };
  state.globalConfig = {
    ...state.globalConfig,
    ...config,
    autoFillEnabled: Utils.parseBoolean(config.preferences["af"]),
    themeName: toThemeName(config.preferences["theme"]),
    bannersDismissed: Utils.parseBannerTimestamps(config.bannersDismissed ?? {}),
  };

  return configureStore({
    reducer: {
      globalConfig,
      testSelection,
      settings,
      testResult,
      testInput,
      proofOfWork,
      oauth,
    },
    devTools: true,
    preloadedState: state,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        thunk: {
          extraArgument: services,
        },
        serializableCheck: false,
      }),
  });
};

type StoreType = ReturnType<typeof getStore>;

// This definition is just for the two type inferences below
let exampleState: StoreType;

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof exampleState.getState>;

// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof exampleState.dispatch;
